import { useState } from 'react';
import api from 'api';
import { useDispatch, useReduxState } from 'hooks';
import { Alert, ModalButtonWithForm } from 'components/common';
import {
  EmailMessageEditor,
  FormGroup,
  RadioGroup,
  Select
} from 'components/common/form';

import type { EmailTemplateData, Group } from 'shared-types';

interface Props {
  administeredGroups: Pick<Group, 'id' | 'title'>[];
  emailTemplates: EmailTemplateData[];
  emailTemplatesDefault: EmailTemplateData;
  updateGroups: () => Promise<void>;
}

const PathGroupsAdd = ({
  administeredGroups,
  emailTemplates,
  emailTemplatesDefault,
  updateGroups
}: Props) => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { path }
  } = useReduxState();

  const [sendEmailNotificationValue, setSendEmailNotificationValue] =
    useState(false);

  const handleSendEmailNotificationChange = (value) => {
    setSendEmailNotificationValue(value);
  };

  const handleSubmit = async (values) => {
    await api.post(`/admin/paths/${path.id}/groups`, {
      groupId: values.groupId
    });

    let emailMessage = null;
    let emailMessageIsWarning = false;

    if (values.sendEmailNotification) {
      const response = await api.post(`/admin/paths/${path.id}/groups-invite`, {
        emailContent: values.email,
        groupId: values.groupId
      });

      emailMessage = `${response.data.sentEmails} ${messages.commonSendEmailInvitesSuccess}`;

      const errorEmails = Object.keys(response.data.errors);

      if (errorEmails && errorEmails.length) {
        emailMessageIsWarning = true;
        emailMessage += `<br /><br /> ${messages.commonSendEmailInvitesError}`;
        errorEmails.forEach((errorEmail) => {
          emailMessage += `<br />${errorEmail}`;
        });
      }
    }

    await updateGroups();

    dispatch.addNotice({
      text: messages.commonAddGroupSuccess,
      type: 'success'
    });

    if (emailMessage) {
      dispatch.addNotice({
        text: emailMessage,
        type: emailMessageIsWarning ? 'warning' : 'success'
      });
    }
  };

  if (!administeredGroups || !emailTemplatesDefault) return null;

  const initialValues = {
    email: {
      ...emailTemplatesDefault.email,
      selectedTemplateIdentifier: emailTemplatesDefault.identifier
    },
    groupId: administeredGroups[0]?.id,
    sendEmailNotification: false
  };

  return (
    <ModalButtonWithForm
      buttonCategory="success"
      buttonIcon="plus"
      buttonSize="header"
      buttonText={messages.commonAddGroup}
      initialValues={initialValues}
      modalActionText={messages.commonAdd}
      modalTitle={messages.commonAddGroup}
      onSubmit={handleSubmit}
      {...(sendEmailNotificationValue ? { modalWidth: 'wide' } : {})}
    >
      {(formState) =>
        administeredGroups && administeredGroups.length ? (
          <div>
            <FormGroup htmlFor="groupId" label={messages.commonGroup}>
              <Select
                field="groupId"
                id="groupId"
                isSearchable={true}
                options={administeredGroups.map((group) => ({
                  label: group.title,
                  value: group.id
                }))}
                style={{ maxWidth: 380 }}
              />
            </FormGroup>

            <FormGroup label={messages.emailNotification}>
              <RadioGroup
                field="sendEmailNotification"
                onChange={handleSendEmailNotificationChange}
                options={[
                  {
                    id: `path-groups-send-email-notification-no`,
                    label: messages.emailNotificationNo,
                    value: false
                  },
                  {
                    id: `path-groups-send-email-notification-yes`,
                    label: messages.emailNotificationGroup,
                    value: true
                  }
                ]}
              />
            </FormGroup>

            <div
              style={
                !formState.values?.sendEmailNotification
                  ? { height: 0, overflow: 'hidden', visibility: 'hidden' }
                  : {}
              }
            >
              <EmailMessageEditor
                availableTemplates={emailTemplates}
                id="path-groups-email-notification"
                style={{ marginTop: 20 }}
              />
            </div>
          </div>
        ) : (
          <Alert text={messages.groupsEmpty} />
        )
      }
    </ModalButtonWithForm>
  );
};

export default PathGroupsAdd;
